import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { isApprovalRequired, submitForApproval } from "@/lib/approvals";
import { ApprovalRequiredModal } from "@/components/pawkt/ApprovalRequiredModal";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";
import { CustomerDeleteButton } from "@/components/pawkt/CustomerDeleteButton";
import { inr, fdate, fdt, initials } from "@/lib/format";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { CallConsentPanel } from "@/components/outbound/CallConsent";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { FormModal } from "@/components/pawkt/FormModal";
import { SectionCard, KV, CompletionBar, Timeline, DocumentsPanel, MoneyRow, CategorySpend } from "@/components/pawkt/three60";
import BusinessOpportunities from "@/components/pawkt/BusinessOpportunities";
import { PackageBalanceWidget } from "@/components/pawkt/PackageCoverage";
import { DiscardInvoiceButton } from "@/components/pawkt/DiscardInvoiceModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InvoiceActions } from "@/components/pawkt/InvoiceActions";
import {
  ArrowLeft, Plus, Phone, Mail, MapPin, PawPrint, Calendar, CalendarPlus, Award, Gift, Ticket, Users, Repeat, PieChart,
} from "lucide-react";
import { toast } from "sonner";
import { CUSTOMER_TYPES } from "@/lib/catalog";
import { BreedSelect } from "@/components/pawkt/BreedSelect";
import { mobileError, normalizeMobile, petDateErrors, todayISO } from "@/lib/phone";
import { AddressPicker, addressError } from "@/components/pawkt/AddressPicker";
import { PetHealthFields, petHealthError } from "@/components/pawkt/VaccinationRecord";
import { PetAgeField } from "@/components/pawkt/PetAgeField";
import { petAgeError } from "@/lib/petAge";

const addrChanged = (c, v) => ["pin", "state", "city", "area", "location_id"].some((k) => (v[k] || "") !== (c[k] || ""));

const SPECIES = ["Dog", "Cat", "Rabbit", "Bird", "Other"].map((s) => ({ value: s, label: s }));
const CONSENT_OPTS = [{ value: "unset", label: "Not answered" }, { value: "true", label: "Yes — opted in" }, { value: "false", label: "No — opted out" }];
const CONSENT_LABEL = { true: "Yes", false: "No" };

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [petOpen, setPetOpen] = useState(false);
  const [editSp] = useSearchParams();
  const [editOpen, setEditOpen] = useState(editSp.get("edit") === "1");
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [tab, setTab] = useState("overview");

  const { data, isLoading, isError } = useQuery({ queryKey: ["customer360", id], queryFn: async () => (await api.get(`/customers/${id}/360`)).data, retry: false });
  const canEdit = hasPerm(user, "customers.edit");
  const canAddPet = hasPerm(user, "pets.create");
  const canInv = hasPerm(user, "invoices.view");

  const addPet = useMutation({
    mutationFn: (body) => api.post("/pets", { ...body, customer_id: id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customer360", id] }); qc.invalidateQueries({ queryKey: ["customer-stats"] }); setPetOpen(false); toast.success("Pet added"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const editCust = useMutation({
    mutationFn: ({ allowDup, ...v }) => {
      // map the tri-state consent selects into the canonical comm_prefs object
      const merged = { ...(data.comm_prefs || {}) };
      ["email", "sms", "whatsapp", "marketing"].forEach((ch) => {
        const sel = v[`consent_${ch}`];
        if (sel === "true") merged[ch] = true;
        else if (sel === "false") merged[ch] = false;
        else if (sel === "unset") delete merged[ch];
      });
      const body = { ...v };
      ["consent_email", "consent_sms", "consent_whatsapp", "consent_marketing"].forEach((k) => delete body[k]);
      body.comm_prefs = merged;
      if (body.mobile !== undefined) body.mobile = normalizeMobile(body.mobile) ?? body.mobile;
      setPendingEdit(body);  // retain proposed values for the approval path
      return api.put(`/customers/${id}`, body, { params: allowDup ? { allow_duplicate: true } : {} });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customer360", id] }); setEditOpen(false); toast.success("Customer updated"); },
    onError: (e, vars) => {
      const d = e?.response?.data?.detail;
      if (isApprovalRequired(e)) { setEditOpen(false); setApprovalOpen(true); }
      else if (d?.error === "CUSTOMER_DUPLICATE_CANDIDATE") {
        const names = (d.candidates || []).map((c) => c.name).join(", ");
        if (window.confirm(`This mobile number is already saved for: ${names}. Save it on this customer too?`)) editCust.mutate({ ...vars, allowDup: true });
      } else toast.error(apiErr(e));
    },
  });
  const submitApproval = useMutation({
    mutationFn: () => submitForApproval("customers.edit", id, pendingEdit),
    onSuccess: () => { setApprovalOpen(false); toast.success("Change submitted for Owner approval."); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const pc = (b) => (b === true ? "true" : b === false ? "false" : "unset");

  if (isError) return <Empty icon={Users} title="Customer not found" subtitle="This customer may have been deleted or is not part of this business." />;
  if (isLoading || !data) return <Loading />;
  const c = data.customer, m = data.commercial;
  const pets = data.pets.items || [];

  return (
    <div>
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1.5 text-sm text-pawkt-text-secondary hover:text-pawkt-text mb-4"><ArrowLeft className="h-4 w-4" /> Back to customers</button>

      {/* Header */}
      <div className="rounded-xl border border-pawkt-border bg-white p-6 shadow-card" data-testid="cust360-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-pawkt-primary text-white flex items-center justify-center text-xl font-bold font-display shrink-0">{initials(c.name)}</div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-pawkt-text">{c.name}{c.preferred_name ? <span className="text-pawkt-text-secondary font-semibold text-lg"> · "{c.preferred_name}"</span> : null}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-pawkt-text-secondary">
              {c.mobile && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.mobile}</span>}
              {c.secondary_phone && <span className="flex items-center gap-1 text-pawkt-text-disabled"><Phone className="h-3.5 w-3.5" /> {c.secondary_phone}</span>}
              {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {c.email}</span>}
              {(c.city || c.address) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[c.address, c.area, c.city, c.state].filter(Boolean).join(", ")}{c.pin ? ` · ${c.pin}` : ""}</span>}
            </div>
            <div className="mt-1 text-xs text-pawkt-text-disabled">
              {c.archived && <span data-testid="cust-archived-badge" className="mr-2 rounded bg-slate-200 px-1.5 py-0.5 font-semibold text-slate-700">Archived {fdate(c.archived_at)}</span>}
              Customer since {fdate(c.created_at)}{c.source ? ` · via ${c.source}` : ""}{data.assigned_staff?.name ? ` · ${data.assigned_staff.name}` : ""}{c.preferred_language ? ` · speaks ${c.preferred_language}` : ""}
            </div>
            <div className="mt-3 max-w-xs">
              <CompletionBar percent={data.profile_completion?.percent ?? 0} testid="cust-completion" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="cust-edit-btn">Edit</Button>}
            {canAddPet && <Button size="sm" variant="outline" onClick={() => setPetOpen(true)} data-testid="cust-addpet-btn" className="gap-1"><Plus className="h-4 w-4" /> Pet</Button>}
            <Button size="sm" variant="outline" onClick={() => navigate("/bookings")} data-testid="cust-book-btn" className="gap-1"><CalendarPlus className="h-4 w-4" /> Book</Button>
            <Button size="sm" variant="outline" onClick={() => setTab("documents")} data-testid="cust-docs-btn">Documents</Button>
            <CustomerDeleteButton customer={c} />
            {!c.archived && <ApprovalRequestButton action="customers.delete" targetId={c.id} targetLabel={c.name} />}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Left column: snapshot */}
        <div className="space-y-5">
          <SectionCard title="Profile & preferences" icon={Mail} testid="cust-profile">
            <KV label="Preferred name" value={c.preferred_name} />
            <KV label="Language" value={c.preferred_language} />
            <KV label="Secondary phone" value={c.secondary_phone} />
            <KV label="Emergency contact" value={[c.emergency_contact_name, c.emergency_contact_relation].filter(Boolean).join(" · ")} />
            <KV label="Emergency phone" value={c.emergency_contact_phone} />
            <KV label="Address" value={[c.address, c.address_line2, c.area, c.city, c.state, c.country, c.pin].filter(Boolean).join(", ")} />
            <div className="mt-2 pt-2 border-t border-pawkt-border">
              <div className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1">Communication consent</div>
              <div className="flex flex-wrap gap-1.5" data-testid="cust-consents">
                {[["WhatsApp", "whatsapp"], ["SMS", "sms"], ["Email", "email"], ["Marketing", "marketing"]].map(([lbl, k]) => {
                  const val = data.comm_prefs?.[k];
                  const tone = val === true ? "bg-pawkt-mint text-pawkt-success-deep" : val === false ? "bg-pawkt-coral text-pawkt-emergency" : "bg-pawkt-elevated text-pawkt-text-disabled";
                  return <span key={k} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${tone}`}>{lbl}: {val === undefined || val === null ? "—" : CONSENT_LABEL[val]}</span>;
                })}
              </div>
              <p className="text-[11px] text-pawkt-text-disabled mt-1.5">Marketing opt-out never blocks service/transactional messages.</p>
            </div>
          </SectionCard>
          <SectionCard title="Relationship" icon={Calendar} testid="cust-relationship">
            <KV label="Customer since" value={fdate(m.customer_since)} />
            <KV label="First visit" value={fdate(m.first_visit)} />
            <KV label="Last visit" value={m.last_visit ? `${fdate(m.last_visit)} (${m.days_since_last_visit}d ago)` : "—"} />
            <KV label="Completed bookings" value={m.completed_bookings} />
            <KV label="Cancelled / no-show" value={m.cancelled_bookings} />
            {canInv && <KV label="Loyalty points" value={c.loyalty_points || 0} />}
          </SectionCard>
          {canInv && (
            <SectionCard title="Commercial" icon={Award} testid="cust-commercial">
              <MoneyRow billed={m.total_billed} paid={m.total_paid} outstanding={m.outstanding} avg={m.avg_transaction_value} />
              <p className="text-[11px] text-pawkt-text-disabled mt-2">Billed is invoiced value; Paid is collected; Outstanding is unpaid balance.</p>
            </SectionCard>
          )}
        </div>

        {/* Right column: tabs */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-pawkt-border bg-white p-5 shadow-card">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-pawkt-elevated flex-wrap h-auto">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="pets" data-testid="tab-pets">Pets ({pets.length})</TabsTrigger>
                <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
                {canInv && <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>}
                <TabsTrigger value="relationships" data-testid="tab-relationships">Relationships</TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                {hasPerm(user, "calls.outbound") && <TabsTrigger value="call-consent" data-testid="tab-call-consent">Call consent</TabsTrigger>}
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4">
                <BusinessOpportunities scope="customer" customerId={id} />
                <PackageBalanceWidget customerId={id} title="Package coverage" />
                <PetStrip pets={pets} navigate={navigate} onAdd={() => setPetOpen(true)} canAddPet={canAddPet} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {canInv && <Mini label="Memberships" value={data.memberships.total} icon={Award} />}
                  <Mini label="Packages" value={data.packages.total} icon={Gift} />
                  {canInv && <Mini label="Coupons used" value={data.coupons.total} icon={Ticket} />}
                  {canInv && <Mini label="Referrals" value={data.referrals.total} icon={Users} />}
                  <Mini label="Follow-ups" value={data.followups.total} icon={Repeat} />
                  {canInv && <Mini label="Loyalty pts" value={c.loyalty_points || 0} icon={Award} />}
                </div>
                {canInv && (
                  <SectionCard title="Category spend & service mix" icon={PieChart} testid="cust-category-spend">
                    <CategorySpend data={data.category_spend} testid="catspend" />
                  </SectionCard>
                )}
              </TabsContent>

              <TabsContent value="pets" className="mt-4">
                <PetStrip pets={pets} navigate={navigate} onAdd={() => setPetOpen(true)} canAddPet={canAddPet} full />
              </TabsContent>

              <TabsContent value="bookings" className="mt-4">
                {!data.bookings.items.length ? <Empty title="No bookings yet" icon={Calendar} /> : (
                  <div className="divide-y divide-pawkt-border">
                    {data.bookings.items.map((b) => (
                      <div key={b.id} className="flex items-center gap-3 py-2.5 text-sm">
                        <div className="flex-1"><div className="font-medium text-pawkt-text">{b.service_name}</div><div className="text-xs text-pawkt-text-disabled">{fdt(b.start_time)} · {b.staff_name || "—"}</div></div>
                        <div className="text-pawkt-text">{inr(b.total)}</div>
                        <StatusBadge status={b.status} />
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {canInv && <TabsContent value="invoices" className="mt-4">
                {!data.invoices.items.length ? <Empty title="No invoices yet" /> : (
                  <div className="divide-y divide-pawkt-border">
                    {data.invoices.items.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-center gap-3 py-3 text-sm" data-testid={`cust-invoice-${r.id}`}>
                        <span className="font-mono text-xs text-pawkt-text w-32">{r.invoice_number}</span>
                        <span className="text-pawkt-text-secondary">{fdate(r.created_at)}</span>
                        <span className="font-semibold">{inr(r.total)}</span>
                        <StatusBadge status={r.status} />
                        <div className="ml-auto flex items-center gap-2">
                          <DiscardInvoiceButton invoice={{ ...r, customer_name: c.name }} onDone={() => qc.invalidateQueries({ queryKey: ["customer360", id] })} />
                          <InvoiceActions invoice={{ ...r, customer_name: c.name }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>}

              <TabsContent value="relationships" className="mt-4 space-y-4">
                {canInv && <RelList title="Memberships" rows={data.memberships.items} render={(x) => `${x.plan_name} · expires ${fdate(x.expiry_date)}`} status={(x) => x.status} empty="No memberships" />}
                <RelList title="Packages" rows={data.packages.items} render={(x) => x.package_name || x.plan_name || "Package"} status={(x) => x.status} empty="No packages" />
                <RelList title="Follow-ups" rows={data.followups.items} render={(x) => `${x.reason || x.source} — ${x.status}`} status={(x) => x.status} empty="No follow-ups" />
                {canInv && <RelList title="Referrals" rows={data.referrals.items} render={(x) => x.referred_name || x.code || "Referral"} empty="No referrals" />}
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <DocumentsPanel base={`/customers/${id}`} canUpload={canEdit} testid="cust-documents" />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <Timeline base={`/customers/${id}`} testid="cust-timeline" />
              </TabsContent>
              <TabsContent value="call-consent" className="mt-4">
                {tab === "call-consent" && <CallConsentPanel customerId={id} mobile={c.mobile} bookings={data.bookings.items} />}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <FormModal open={petOpen} onOpenChange={setPetOpen} title={`Add pet for ${c.name}`} testid="pet-modal"
        initial={{ species: "Dog" }} submitLabel="Add pet"
        fields={[
          { name: "name", label: "Pet name", placeholder: "Bruno", full: true },
          { name: "petbreed", label: "", type: "custom", full: true, render: (v, set) => <BreedSelect v={v} set={set} /> },
          { name: "gender", label: "Gender", type: "select", options: [{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }] },
          { name: "weight", label: "Weight (kg)", type: "number" },
          { name: "color", label: "Color" },
          { name: "dob", label: "Date of birth (optional)", type: "date", max: todayISO(), validate: (_x, v) => petDateErrors(v).dob || "" },
          { name: "adoption_date", label: "Adoption date (optional)", type: "date", max: todayISO(), validate: (_x, v) => petDateErrors(v).adoption_date || "" },
          { name: "age", label: "", type: "custom", full: true, validate: (_x, v) => petAgeError(v),
            render: (v, set) => <PetAgeField value={v} testid="add-pet-age" showError={!!(v.name || "").trim() || v.age_years != null || v.age_months != null} onChange={(p) => Object.entries(p).forEach(([k, x]) => set(k, x))} /> },
          { name: "must_board_alone", label: "", type: "custom", full: true, render: (v, set) => (
            <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={!!v.must_board_alone} onChange={(e) => set("must_board_alone", e.target.checked)} data-testid="field-must_board_alone" /> Must board alone (never shares a kennel/room)</label>) },
          { name: "health", label: "", type: "custom", full: true, validate: (_x, v) => petHealthError(v),
            render: (v, set) => <PetHealthFields pet={v} testid="add-pet-health" onChange={(p) => Object.entries(p).forEach(([k, x]) => set(k, x))} /> },
        ]}
        onSubmit={(v) => addPet.mutateAsync(v)} />

      <FormModal open={editOpen} onOpenChange={setEditOpen} title="Edit customer" testid="cust-edit-modal"
        initial={{ name: c.name, mobile: c.mobile, email: c.email, secondary_phone: c.secondary_phone,
          preferred_name: c.preferred_name, preferred_language: c.preferred_language,
          emergency_contact_name: c.emergency_contact_name, emergency_contact_phone: c.emergency_contact_phone,
          emergency_contact_relation: c.emergency_contact_relation,
          address: c.address, address_line2: c.address_line2, area: c.area, city: c.city,
          state: c.state, country: c.country, pin: c.pin, location_id: c.location_id || "",
          birthday: c.birthday, anniversary: c.anniversary, source: c.source, customer_type: c.customer_type, notes: c.notes,
          consent_email: pc(data.comm_prefs?.email), consent_sms: pc(data.comm_prefs?.sms),
          consent_whatsapp: pc(data.comm_prefs?.whatsapp), consent_marketing: pc(data.comm_prefs?.marketing) }}
        submitLabel="Save changes"
        fields={[
          { name: "name", label: "Name", full: true },
          { name: "preferred_name", label: "Preferred name" },
          { name: "preferred_language", label: "Preferred language" },
          { name: "mobile", label: "Primary phone", inputMode: "tel", validate: (x) => mobileError(x, { allowBlank: !(c.mobile || "").trim() }),
            hint: (c.mobile || "").trim() && normalizeMobile(c.mobile) === null ? "The saved number is invalid — correct it to save." : "10 digits. +91 or a leading 0 is accepted and removed." },
          { name: "secondary_phone", label: "Secondary phone" },
          { name: "email", label: "Email", full: true },
          { name: "emergency_contact_name", label: "Emergency contact" },
          { name: "emergency_contact_relation", label: "Relation" },
          { name: "emergency_contact_phone", label: "Emergency phone" },
          { name: "customer_type", label: "Type", type: "select", options: CUSTOMER_TYPES },
          { name: "address", label: "Address line 1", full: true },
          { name: "address_line2", label: "Address line 2", full: true },
          { name: "location", type: "custom", full: true,
            validate: (_x, v) => (addrChanged(c, v) ? addressError(v) : ""),
            render: (v, set) => (
              <div>
                {!c.location_id && (c.pin || c.city || c.area) && !addrChanged(c, v) && <p className="mb-1 text-xs text-amber-700" data-testid="cust-legacy-address">Saved address: {[c.area, c.city, c.state, c.pin].filter(Boolean).join(", ")} — select the PIN code below to update it.</p>}
                <AddressPicker value={v} testid="cust-edit-addr" onChange={(p) => Object.entries(p).forEach(([k, x]) => set(k, x))} error={addrChanged(c, v) ? addressError(v) : ""} />
              </div>) },
          { name: "source", label: "Source" },
          { name: "consent_whatsapp", label: "WhatsApp consent", type: "select", options: CONSENT_OPTS },
          { name: "consent_sms", label: "SMS consent", type: "select", options: CONSENT_OPTS },
          { name: "consent_email", label: "Email consent", type: "select", options: CONSENT_OPTS },
          { name: "consent_marketing", label: "Marketing consent", type: "select", options: CONSENT_OPTS },
          { name: "notes", label: "Notes", type: "textarea", full: true },
        ]}
        onSubmit={(v) => editCust.mutateAsync({ ...v, allowDup: false })} />

      <ApprovalRequiredModal open={approvalOpen} onOpenChange={setApprovalOpen} entity="customer change"
        busy={submitApproval.isPending} testid="cust-approval-modal"
        onSubmit={() => submitApproval.mutate()} />
    </div>
  );
}

function PetStrip({ pets, navigate, onAdd, canAddPet, full }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-base font-bold text-pawkt-text">Pets</h3>
        {canAddPet && <Button size="sm" variant="ghost" onClick={onAdd} data-testid="add-pet-btn" className="gap-1"><Plus className="h-4 w-4" /> Add</Button>}
      </div>
      {!pets.length ? <Empty title="No pets yet" subtitle="Add a pet to start the relationship." icon={PawPrint} /> : (
        <div className={`grid gap-3 ${full ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
          {pets.map((p) => (
            <button key={p.id} onClick={() => navigate(`/pets/${p.id}`)} data-testid={`pet-card-${p.id}`}
              className="flex items-center gap-3 rounded-lg border border-pawkt-border p-3 text-left hover:border-pawkt-primary hover:bg-pawkt-primary-soft/40 transition-colors">
              <div className="h-11 w-11 rounded-xl bg-pawkt-primary-soft flex items-center justify-center overflow-hidden shrink-0">
                {p.photo ? <img src={p.photo} alt="" className="h-full w-full object-cover" /> : <PawPrint className="h-5 w-5 text-pawkt-primary" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-pawkt-text truncate">{p.name}</div>
                <div className="text-xs text-pawkt-text-disabled truncate">{[p.species, p.breed].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="text-right shrink-0"><div className="text-[10px] uppercase text-pawkt-text-disabled">Profile</div><div className="text-sm font-bold text-pawkt-primary-deep">{p.profile_completion}%</div></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-pawkt-border bg-pawkt-canvas p-3 text-center">
      <Icon className="h-4 w-4 text-pawkt-primary mx-auto mb-1" />
      <div className="font-display text-lg font-extrabold text-pawkt-text">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-pawkt-text-secondary">{label}</div>
    </div>
  );
}

function RelList({ title, rows, render, status, empty }) {
  return (
    <div data-testid={`rel-${title.toLowerCase()}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1">{title}</div>
      {!rows?.length ? <p className="text-sm text-pawkt-text-disabled py-2">{empty}</p> : (
        <div className="divide-y divide-pawkt-border">
          {rows.map((x, i) => (
            <div key={x.id || i} className="flex items-center gap-2 py-2 text-sm">
              <span className="flex-1 text-pawkt-text">{render(x)}</span>
              {status && status(x) && <StatusBadge status={status(x)} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
